import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Pencil, LayoutGrid } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { DeckMetadata } from "@/types/decks"

interface DeckCardProps {
  deck: {
    id: number;
    name: string;
    tagline: string;
    universe_name: string;
    card_count: number;
    is_public: boolean;
    is_collaborative: boolean;
    metadata?: DeckMetadata | null;
    created_at: string;
  }
  onEdit: () => void;
}

export function DeckCard({ deck, onEdit }: DeckCardProps) {
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-semibold">{deck.name}</h3>
            <p className="text-sm text-muted-foreground">{deck.tagline || 'No tagline available'}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="p-3 sm:p-2"
          >
            <Pencil className="h-6 w-6 sm:h-4 sm:w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-grow">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded ${
              Number(deck.is_public) === 1
                ? 'bg-green-500/10 text-green-500' 
                : 'bg-blue-500/10 text-blue-500'
            }`}>
              {Number(deck.is_public) === 1 ? 'Public' : 'Private'}
            </span>
            <span className={`text-xs px-2 py-1 rounded ${
              Number(deck.is_collaborative) === 1
                ? 'bg-purple-500/10 text-purple-500' 
                : 'bg-slate-500/10 text-slate-500'
            }`}>
              {Number(deck.is_collaborative) === 1 ? 'Collab' : 'Solo'}
            </span>
          </div>
          {deck.metadata?.tags && deck.metadata.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {deck.metadata.tags.map((tag: string) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
          {deck.metadata?.categories && deck.metadata.categories.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {deck.metadata.categories.map((category: string) => (
                <Badge key={category} variant="secondary" className="text-xs">
                  {category}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <LayoutGrid className="h-4 w-4" />
          {deck.card_count} cards
        </div>
        <div className="text-sm text-muted-foreground">
          {new Date(deck.created_at).toLocaleDateString()}
        </div>
      </CardFooter>
    </Card>
  )
} 